import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Dashboard/Sidebar/Sidebar";
import Header from "../../components/Dashboard/Header/Header";
import SummaryCard from "../../components/Dashboard/SummaryCard/SummaryCard";
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../components/Common/Pagination/Pagination';
import api from '../../api/axios';
import {
  FolderKanban,
  FileText,
  ShoppingCart,
  Award,
  Package,
  ArrowUpRight,
  AlertTriangle,
  Plus,
  ChevronRight,
  Sparkles,
  Layers,
  IndianRupee,
  CheckSquare,
  TrendingUp,
  Eye
} from 'lucide-react';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Dashboard Data State (Structured for single GET /dashboard/stats API endpoint)
  const [dashboardData, setDashboardData] = useState({
    stats: {
      active_projects: 0,
      total_lois: 0,
      purchase_orders: 0,
      manufacturer_clearances: 0,
      material_receipts: 0
    },
    recent_projects: [],
    pipeline_stages: [],
    action_items: []
  });

  const [activeProjects, setActiveProjects] = useState([]);
  const [projectsPagination, setProjectsPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 5
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    fetchDashboardData();
    fetchActiveProjects(1);
  }, []);

  const fetchActiveProjects = async (page = 1) => {
    try {
      const res = await api.get('/dashboard/active-projects', {
        params: { page, per_page: projectsPagination.per_page || 5 },
        skipLoader: true
      });
      if (res.data && res.data.success) {
        const payload = res.data.data;
        const list = Array.isArray(payload) ? payload : (payload?.data || []);
        setActiveProjects(list);
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          setProjectsPagination({
            current_page: payload.current_page || page,
            last_page: payload.last_page || 1,
            total: payload.total || list.length,
            per_page: payload.per_page || 5
          });
        }
      }
    } catch (error) {
      console.warn('Active projects API error:', error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/stats', { skipLoader: true });
      if (res.data && res.data.success) {
        setDashboardData(res.data.data);
      }
    } catch (error) {
      console.error('Dashboard API error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format currency values safely
  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    if (Math.abs(num) >= 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    }
    if (Math.abs(num) >= 100000) {
      return `₹${(num / 100000).toFixed(2)} L`;
    }
    return `₹${num.toLocaleString('en-IN')}`;
  };

  // Convert stats object to SummaryCard props format
  const summaryCards = [
    {
      icon: <FolderKanban size={20} color="#fff" />,
      label: 'Active Projects',
      value: dashboardData.stats.active_projects || 0,
      trend: 'Active',
      trendType: 'up',
      iconBg: '#3b82f6'
    },
    {
      icon: <TrendingUp size={20} color="#fff" />,
      label: 'Overall Progress',
      value: `${dashboardData.stats.overall_project_progress || 0}%`,
      trend: 'Avg Progress',
      trendType: 'up',
      iconBg: '#8b5cf6'
    },
    {
      icon: <ShoppingCart size={20} color="#fff" />,
      label: 'Total Purchase',
      value: formatCurrency(dashboardData.stats.total_purchase),
      trend: 'PO Cost',
      trendType: 'up',
      iconBg: '#ff9d42'
    },
    {
      icon: <IndianRupee size={20} color="#fff" />,
      label: 'Total Sales',
      value: formatCurrency(dashboardData.stats.total_sales),
      trend: 'Invoiced',
      trendType: 'up',
      iconBg: '#10b981'
    },
    {
      icon: <Sparkles size={20} color="#fff" />,
      label: 'Turnover',
      value: formatCurrency(dashboardData.stats.turnover),
      trend: 'Net Turnover',
      trendType: (dashboardData.stats.turnover || 0) >= 0 ? 'up' : 'down',
      iconBg: '#06b6d4'
    }
  ];

  return (
    <div className={`${styles['dashboard-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay-dashboard']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />
        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Header Banner */}
          <div className={styles['dashboard-header-area']}>
            <div className={styles['welcome-section']}>
              <h1>Welcome back, {user?.name || 'Admin'} 👋</h1>
              <p>Here is an overview of your projects, purchase orders, and clearances for {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.</p>
            </div>
            <div className={styles['header-actions-group']}>
              <button
                onClick={() => navigate('/projects/create')}
                className={styles['dashboard-btn-primary']}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  backgroundColor: '#ff9d42',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  boxShadow: '0 4px 12px rgba(255, 157, 66, 0.25)'
                }}
              >
                <Plus size={18} /> New Project
              </button>
              <button
                onClick={() => navigate('/purchase-orders/create')}
                className={styles['dashboard-btn-secondary']}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  backgroundColor: '#fff',
                  color: '#1e293b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                <ShoppingCart size={18} /> Create PO
              </button>
            </div>
          </div>

          {/* Top Key Metrics */}
          <div className={styles['summary-section']}>
            {summaryCards.map((data, index) => (
              <SummaryCard key={index} {...data} />
            ))}
          </div>

          {/* Core Dashboard Content Layout */}
          <div className={styles['charts-section']}>
            {/* Left: Active Projects Status */}
            <div className={styles['dashboard-card']}>
              <div className={styles['card-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FolderKanban size={20} color="#ff9d42" />
                  <h3 className={styles['card-title']}>Active Projects Overview</h3>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className={`${styles['table-wrapper']} ${styles['desktop-only-table']}`}>
                <table className={styles['custom-table']}>
                  <thead>
                    <tr>
                      <th>Project & Code</th>
                      <th>Client</th>
                      <th>Status</th>
                      <th>Progress</th>
                      {/* <th style={{ textAlign: 'right' }}>Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {(activeProjects.length > 0 ? activeProjects : (dashboardData.recent_projects || [])).length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                          No active projects found.
                        </td>
                      </tr>
                    ) : (
                      (activeProjects.length > 0 ? activeProjects : (dashboardData.recent_projects || [])).map(proj => {
                        const progressVal = proj.progress_percent ?? proj.progress ?? 0;
                        return (
                          <tr key={proj.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${proj.id}`)}>
                            <td>
                              <div style={{ fontWeight: '700', fontSize: '0.875rem', color: '#0f172a' }}>{proj.name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{proj.code}</div>
                            </td>
                            <td style={{ color: '#334155', fontWeight: '500' }}>
                              {proj.client || 'N/A'}
                            </td>
                            <td>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.725rem',
                                fontWeight: '700',
                                whiteSpace: 'nowrap',
                                backgroundColor: proj.status === 'On Track' || proj.status === 'Near Completion' ? '#ecfdf5' : proj.status === 'Delayed' ? '#fef2f2' : '#eff6ff',
                                color: proj.status === 'On Track' || proj.status === 'Near Completion' ? '#059669' : proj.status === 'Delayed' ? '#dc2626' : '#2563eb',
                                border: proj.status === 'On Track' || proj.status === 'Near Completion' ? '1px solid #a7f3d0' : proj.status === 'Delayed' ? '1px solid #fecaca' : '1px solid #bfdbfe'
                              }}>
                                {proj.status || 'Active'}
                              </span>
                            </td>
                            <td style={{ width: '130px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ flex: 1, height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ width: `${progressVal}%`, height: '100%', background: progressVal > 70 ? '#10b981' : progressVal < 40 ? '#f59e0b' : '#3b82f6' }}></div>
                                </div>
                                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#475569' }}>{progressVal}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards List View */}
              <div className={styles['mobile-projects-list']}>
                {(activeProjects.length > 0 ? activeProjects : (dashboardData.recent_projects || [])).length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                    No active projects found.
                  </div>
                ) : (
                  (activeProjects.length > 0 ? activeProjects : (dashboardData.recent_projects || [])).map(proj => {
                    const progressVal = proj.progress_percent ?? proj.progress ?? 0;
                    return (
                      <div
                        key={proj.id}
                        className={styles['mobile-project-card']}
                        onClick={() => navigate(`/projects/${proj.id}`)}
                      >
                        <div className={styles['mobile-card-top']}>
                          <div>
                            <div className={styles['mobile-proj-name']}>{proj.name}</div>
                            <div className={styles['mobile-proj-code']}>{proj.code}</div>
                          </div>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: '700',
                            whiteSpace: 'nowrap',
                            backgroundColor: proj.status === 'On Track' || proj.status === 'Near Completion' ? '#ecfdf5' : proj.status === 'Delayed' ? '#fef2f2' : '#eff6ff',
                            color: proj.status === 'On Track' || proj.status === 'Near Completion' ? '#059669' : proj.status === 'Delayed' ? '#dc2626' : '#2563eb',
                            border: proj.status === 'On Track' || proj.status === 'Near Completion' ? '1px solid #a7f3d0' : proj.status === 'Delayed' ? '1px solid #fecaca' : '1px solid #bfdbfe'
                          }}>
                            {proj.status || 'Active'}
                          </span>
                        </div>

                        <div className={styles['mobile-card-details']}>
                          <div className={styles['mobile-detail-item']}>
                            <span className={styles['mobile-detail-label']}>Client</span>
                            <span className={styles['mobile-detail-val']}>{proj.client || 'N/A'}</span>
                          </div>
                          <div className={styles['mobile-detail-item']}>
                            <span className={styles['mobile-detail-label']}>Progress</span>
                            <div className={styles['mobile-progress-wrapper']}>
                              <div className={styles['mobile-progress-bar']}>
                                <div style={{ width: `${progressVal}%`, height: '100%', background: progressVal > 70 ? '#10b981' : progressVal < 40 ? '#f59e0b' : '#3b82f6' }}></div>
                              </div>
                              <span className={styles['mobile-progress-txt']}>{progressVal}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {(projectsPagination.last_page > 1 || projectsPagination.total > 0) && (
                <div style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                  <Pagination
                    currentPage={projectsPagination.current_page}
                    totalPages={projectsPagination.last_page}
                    totalItems={projectsPagination.total}
                    itemsPerPage={projectsPagination.per_page}
                    onPageChange={(page) => fetchActiveProjects(page)}
                    showSummary={true}
                  />
                </div>
              )}
            </div>

            {/* Right: Stage Pipeline Breakdown */}
            <div className={styles['dashboard-card']}>
              <div className={styles['card-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Layers size={20} color="#3b82f6" />
                  <h3 className={styles['card-title']}>Project Execution Pipeline</h3>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
                {(dashboardData.pipeline_stages || []).length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                    No pipeline stages data available.
                  </div>
                ) : (
                  (dashboardData.pipeline_stages || []).map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>
                        <span>{item.stage}</span>
                        <span style={{ color: item.color }}>{item.count} {item.unit || ''}</span>
                      </div>
                      <div style={{ height: '8px', width: '100%', backgroundColor: '#f1f5f9', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, (item.count / 45) * 100)}%`, height: '100%', backgroundColor: item.color, borderRadius: '6px' }}></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Bottom Section: Pending Tasks & Quick Shortcuts */}
          <div className={styles['bottom-section']}>
            {/* Left: Pending Approvals & Clearance Alerts */}
            <div className={styles['dashboard-card']}>
              <div className={styles['card-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <AlertTriangle size={20} color="#ef4444" />
                  <h3 className={styles['card-title']}>Action Required & Critical Alerts</h3>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                {(dashboardData.action_items || []).length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                    No pending action items.
                  </div>
                ) : (
                  (dashboardData.action_items || []).map((action, idx) => (
                    <div
                      key={idx}
                      onClick={() => navigate(action.link)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justify: 'space-between',
                        padding: '14px 16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        borderLeft: `4px solid ${action.priority === 'Urgent' ? '#ef4444' : action.priority === 'High' ? '#f59e0b' : '#3b82f6'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1e293b' }}>{action.title}</div>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '0.78rem', color: '#64748b' }}>
                          <span>📁 {action.category}</span>
                          <span>⏰ {action.date}</span>
                        </div>
                      </div>
                      <ArrowUpRight size={18} color="#94a3b8" />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right: Quick Module Shortcuts */}
            <div className={styles['dashboard-card']}>
              <div className={styles['card-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Sparkles size={20} color="#8b5cf6" />
                  <h3 className={styles['card-title']}>Quick Navigation</h3>
                </div>
              </div>
              <div className={styles['quick-nav-grid']}>
                {[
                  { label: 'LOIs', path: '/projects/lois', icon: <FileText size={18} color="#3b82f6" /> },
                  { label: 'Purchase Orders', path: '/purchase-orders', icon: <ShoppingCart size={18} color="#ff9d42" /> },
                  { label: 'Manufacturer Clearance', path: '/manufacturer-clearance', icon: <Award size={18} color="#10b981" /> },
                  { label: 'Inspections', path: '/inspections', icon: <CheckSquare size={18} color="#8b5cf6" /> },
                  { label: 'Material Receipts', path: '/receipts', icon: <Package size={18} color="#06b6d4" /> },
                  { label: 'Vendor Payments', path: '/payment/vendor-payments', icon: <IndianRupee size={18} color="#ec4899" /> }
                ].map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => navigate(item.path)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '8px',
                      padding: '14px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.85rem',
                      color: '#1e293b'
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

        </main>
      </div>
    </div>
  );
};

export default Dashboard;